import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Share2 } from "lucide-react";

import "./DropView.css";
import { useAuth } from "../context/AuthContext";
import SkeletonCard from "../components/SkeletonCard";
import DropMedia from "../components/DropMedia";
import WatermarkOverlay from "../components/WatermarkOverlay";
import DropCommentsSection from "../components/DropCommentsSection";
import DropReportAction from "../components/DropReportAction";
import ShareModal from "../components/ShareModal";
import TierSubscriptionModal from "../components/Subscription/TierSubscriptionModal";
import DropUnlockChoiceModal from "../components/DropUnlock/DropUnlockChoiceModal";
import TipJarSheet from "../components/tips/TipJarSheet";
import { NicheIcon } from "../components/niche/NicheIcons";
import { getNicheMeta } from "../components/niche/nicheMeta";
import {
  freeUnlockCreditsForPrice,
  SPEND,
} from "../constants/creditsConfig";
import {
  fetchCreditSummary,
  unlockDropWithCredits,
} from "../services/creditsService";
import {
  checkPaidDropUnlock,
  purchaseDropAccess,
} from "../services/dropUnlockService";
import {
  checkCreditUnlock,
  checkIsFollowing,
  checkSupporterAccess,
  checkUserReaction,
  listenToDrop,
  recordDropView,
  toggleDropLike,
  toggleDropSave,
} from "../services/socialService";
import { toggleFollow } from "../services/followService";
import {
  fetchAlsoWatchedByFollowing,
  fetchCreatorDropCatalog,
  fetchCreatorDropCount,
  fetchDropWithCreator,
  fetchFirstViewRecord,
  isPaidArchive,
} from "../services/dropViewService";
import {
  contentTypeLabel,
  dropMaxViews,
  dropViewCount,
  formatCount,
  getDropStatus,
  timeAgo,
} from "../pages/creator/creatorHelpers";
import { setPageTitle } from "../components/DocumentTitle";
import { bypassesContentPaywall } from "../utils/roleUtils";
import {
  isTierHigherOrEqual,
  normalizeTierId,
} from "../constants/subscriptionTiers";
import { DURATION, EASE_CSS, useMotionSafe } from "../utils/motionTokens";

const CLOSING_SOON_THRESHOLD = 15;
const SHEET_PEEK_PX = 56;
const AXIS_LOCK_PX = 8;
const SWIPE_X_THRESHOLD = 60;
const SWIPE_Y_THRESHOLD = 50;
/* When the sheet is already open, a handle pointerdown must not steal
   the gesture until the finger clearly pulls down. 12px is just past
   AXIS_LOCK so we know direction, but well under SWIPE_Y_THRESHOLD so
   a deliberate close still feels immediate once it engages. */
const OPEN_HANDLE_CLOSE_INTENT_PX = 12;

/* ── Helpers ── */

function previewThumb(d) {
  if (!d) return "";
  return (
    d.thumbnailUrl
    || d.thumbnail
    || d.coverUrl
    || d.coverImage
    || d.imageUrl
    || d.photoUrl
    || (d.contentType === "photo" || d.contentType === "image" ? d.mediaUrl : "")
    || ""
  );
}

function priceLabel(drop) {
  if (drop?.isFree || !drop?.price) return "Free";
  return `$${Number(drop.price).toFixed(2)}`;
}

function archivePriceLabel(drop) {
  const p = drop?.archivePrice || drop?.price;
  return p ? `$${Number(p).toFixed(2)}` : "$9.00";
}

function isFreeDrop(drop) {
  if (!drop) return false;
  if (drop.accessType === "free" || drop.isFree === true) return true;
  if (drop.accessType === "paid" || drop.accessType === "subscription") return false;
  if (drop.tierRequired) return false;
  const price = Number(drop.price);
  return !price || price === 0 || drop.price === "free";
}

function isSubscriptionDrop(drop) {
  if (!drop) return false;
  return (
    drop.accessType === "subscription"
    || Boolean(drop.tierRequired)
    || Boolean(drop.unlock_tier)
  );
}

function isPaidDrop(drop) {
  if (!drop || isFreeDrop(drop) || isSubscriptionDrop(drop)) return false;
  const price = Number(drop?.price) || 0;
  return (
    drop?.accessType === "paid" ||
    (price > 0 && drop?.accessType !== "free" && !drop?.isFree)
  );
}

function requiredTierForDrop(drop) {
  return normalizeTierId(drop?.tierRequired || drop?.unlock_tier || "supporter");
}

function accessLabel(drop) {
  if (isFreeDrop(drop)) return "Open to everyone";
  if (isSubscriptionDrop(drop)) return "Subscribers only";
  return "First 100 only";
}

function initials(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "C";
}

function isFoundingCreator(creator) {
  return Boolean(
    creator?.isFoundingContributor || creator?.foundingCreator || creator?.isFoundingMember
  );
}

function LockIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 018 0v3" />
    </svg>
  );
}

function HeartIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.8 4.6a5.5 5.5 0 00-7.8 0L12 5.6l-1-1a5.5 5.5 0 00-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 000-7.8z" />
    </svg>
  );
}

function KeepIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
    </svg>
  );
}

function TipIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 8h1a4 4 0 010 8h-1" />
      <path d="M3 8h14v9a4 4 0 01-4 4H7a4 4 0 01-4-4V8z" />
      <line x1="6" y1="1" x2="6" y2="4" />
      <line x1="10" y1="1" x2="10" y2="4" />
      <line x1="14" y1="1" x2="14" y2="4" />
    </svg>
  );
}

function Avatar({ src, name, className }) {
  if (src) return <img className={className} src={src} alt={name || ""} />;
  return (
    <div className={`${className} dv-avatar-fallback`} aria-hidden="true">
      {initials(name)}
    </div>
  );
}

export default function DropView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const unlockedDrops = userProfile?.unlockedDrops;
  const { prefersReduced } = useMotionSafe();

  const [drop, setDrop] = useState(null);
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessLoading, setAccessLoading] = useState(true);

  const [isSupporter, setIsSupporter] = useState(false);
  const [hasCreditUnlock, setHasCreditUnlock] = useState(false);
  const [hasPaidUnlock, setHasPaidUnlock] = useState(false);
  const [firstViewRecord, setFirstViewRecord] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [viewRecorded, setViewRecorded] = useState(false);

  const [alsoWatched, setAlsoWatched] = useState({ count: 0, viewers: [] });
  const [creatorDropCount, setCreatorDropCount] = useState(0);

  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [engagementBusy, setEngagementBusy] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [showUnlockChoice, setShowUnlockChoice] = useState(false);
  const [payModalKind, setPayModalKind] = useState("unlock"); // unlock | archive
  const [payModalSeed, setPayModalSeed] = useState(null);
  const [showTierOverlay, setShowTierOverlay] = useState(false);
  const [tierModalInitial, setTierModalInitial] = useState(null);

  const [creditSummary, setCreditSummary] = useState(null);
  const [creditUnlocking, setCreditUnlocking] = useState(false);
  const [directUnlocking, setDirectUnlocking] = useState(false);
  const [unlockToast, setUnlockToast] = useState(null);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDragging, setSheetDragging] = useState(false);
  const [sheetOffset, setSheetOffset] = useState(null);
  const [frostOverlay, setFrostOverlay] = useState(false);

  const [catalog, setCatalog] = useState([]);
  const [trackDragging, setTrackDragging] = useState(false);
  const [trackOffset, setTrackOffset] = useState(0);

  const viewportRef = useRef(null);
  const sheetRef = useRef(null);
  const dragRef = useRef(null);
  const catalogRef = useRef([]);
  const catalogIndexRef = useRef(-1);
  /* Suppress the handle's click-to-toggle after an open-sheet handle
     gesture that moved (deferred close abandoned or engaged) so a
     drag doesn't also fire click and flip the sheet. */
  const suppressHandleClickRef = useRef(false);

  /* ── Load drop + creator ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await fetchDropWithCreator(id);
        if (!cancelled) {
          setDrop(result.drop);
          setCreator(result.creator);
        }
      } catch (err) {
        console.error("DropView load:", err);
        if (!cancelled) setDrop(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!id) return undefined;
    return listenToDrop(id, (data) => setDrop((prev) => ({ ...prev, ...data, id })));
  }, [id]);

  useEffect(() => {
    setViewRecorded(false);
    setSheetOpen(false);
    setFrostOverlay(false);
    setTrackOffset(0);
    setTrackDragging(false);
  }, [id]);

  useEffect(() => {
    if (!drop?.creatorId) {
      setCatalog([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchCreatorDropCatalog(drop.creatorId, drop.id, 24);
        if (cancelled) return;
        // Ensure the live drop is present even if the capped query missed it
        const hasCurrent = rows.some((r) => r.id === drop.id);
        const next = hasCurrent
          ? rows
          : [{ id: drop.id, ...drop }, ...rows].sort(
            (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
          );
        setCatalog(next);
      } catch (err) {
        console.error("DropView catalog:", err);
        if (!cancelled) setCatalog([{ id: drop.id, ...drop }]);
      }
    })();
    return () => { cancelled = true; };
  }, [drop?.creatorId, drop?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- drop fields used only for fallback stub

  useEffect(() => {
    catalogRef.current = catalog;
  }, [catalog]);

  const catalogIndex = catalog.findIndex((d) => d.id === id);
  useEffect(() => {
    catalogIndexRef.current = catalogIndex;
  }, [catalogIndex]);

  const goToCatalogIndex = (nextIndex) => {
    const rows = catalogRef.current;
    if (nextIndex < 0 || nextIndex >= rows.length) return;
    const target = rows[nextIndex];
    if (!target?.id || target.id === id) return;
    navigate(`/drop/${target.id}`, { replace: true });
  };

  const goPrev = () => goToCatalogIndex(catalogIndexRef.current - 1);
  const goNext = () => goToCatalogIndex(catalogIndexRef.current + 1);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target?.closest?.("input, textarea, [contenteditable='true']")) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps -- goPrev/goNext use refs

  useEffect(() => {
    if (!drop) return;
    const label = drop.title || creator?.displayName || "Drop";
    setPageTitle(`100ViewsOnly — ${label}`);
  }, [drop, creator?.displayName]);

  useEffect(() => {
    if (!currentUser) {
      setCreditSummary(null);
      return;
    }
    fetchCreditSummary(currentUser.uid, userProfile?.role === "creator" || userProfile?.isCreator)
      .then(setCreditSummary)
      .catch(() => setCreditSummary(null));
  }, [currentUser, userProfile]);

  useEffect(() => {
    if (!unlockToast) return undefined;
    const t = setTimeout(() => setUnlockToast(null), 4000);
    return () => clearTimeout(t);
  }, [unlockToast]);

  useEffect(() => {
    if (!drop?.creatorId) return;
    let cancelled = false;
    (async () => {
      try {
        const count = await fetchCreatorDropCount(drop.creatorId);
        if (!cancelled) setCreatorDropCount(count);
      } catch (err) {
        console.error("DropView drop count:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [drop?.creatorId]);

  /* ── Access + social data ── */
  useEffect(() => {
    if (!drop || loading) return;
    if (!currentUser) { setAccessLoading(false); return; }

    let cancelled = false;
    (async () => {
      setAccessLoading(true);
      try {
        const [
          watched,
          supporterResult,
          creditResult,
          paidResult,
          firstView,
          following,
          likedRes,
          savedRes,
        ] = await Promise.all([
          fetchAlsoWatchedByFollowing(currentUser.uid, drop.id, 3),
          checkSupporterAccess(currentUser.uid, drop.creatorId),
          checkCreditUnlock(currentUser.uid, drop.id),
          checkPaidDropUnlock(currentUser.uid, drop.id),
          fetchFirstViewRecord(currentUser.uid, drop.id),
          checkIsFollowing(currentUser.uid, drop.creatorId),
          checkUserReaction({ type: "likes", momentId: drop.id, userId: currentUser.uid }),
          checkUserReaction({ type: "saves", momentId: drop.id, userId: currentUser.uid }),
        ]);
        if (cancelled) return;
        setAlsoWatched(watched);
        setIsSupporter(supporterResult?.isSupporter || false);
        setHasCreditUnlock(creditResult || false);
        setHasPaidUnlock(paidResult || Boolean(unlockedDrops?.[drop.id]));
        setFirstViewRecord(firstView);
        setIsFollowing(following || false);
        setLiked(likedRes || false);
        setSaved(savedRes || false);
      } catch (err) {
        console.error("DropView access:", err);
      } finally {
        if (!cancelled) setAccessLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [drop, currentUser, loading, unlockedDrops]);

  /* ── Derived state ── */
  const isOwner = Boolean(currentUser && drop && currentUser.uid === drop.creatorId);
  const isClosed = drop ? getDropStatus(drop) === "closed" : false;
  const views = drop ? dropViewCount(drop) : 0;
  const maxViews = drop ? dropMaxViews(drop) : 100;
  const viewsLeft = Math.max(maxViews - views, 0);
  const progress = drop ? Math.min(100, Math.round((views / maxViews) * 100)) : 0;
  const closingSoon = !isClosed && viewsLeft <= CLOSING_SOON_THRESHOLD;

  const userTierForCreator = drop?.creatorId
    ? normalizeTierId(userProfile?.subscriptionTiers?.[drop.creatorId])
    : null;
  const dropRequiredTier = drop ? requiredTierForDrop(drop) : null;
  const hasExplicitTierRequirement = Boolean(drop?.tierRequired || drop?.unlock_tier);
  const hasRequiredTierAccess = Boolean(
    drop
    && isSubscriptionDrop(drop)
    && (
      isTierHigherOrEqual(userTierForCreator, dropRequiredTier)
      || (isSupporter && !hasExplicitTierRequirement)
    )
  );

  const hasAccess = Boolean(
    drop && currentUser && (
      isOwner ||
      bypassesContentPaywall(userProfile) ||
      isSupporter ||
      hasCreditUnlock ||
      hasPaidUnlock ||
      Boolean(userProfile?.unlockedDrops?.[drop.id]) ||
      Boolean(firstViewRecord) ||
      hasRequiredTierAccess
    )
  );

  const gated = Boolean(drop) && !hasAccess;
  const closedForever = gated && isClosed && !isPaidArchive(drop?.afterClose);
  const closedArchive = gated && isClosed && isPaidArchive(drop?.afterClose);
  const openGate = gated && !isClosed;

  const showAlsoWatched = hasAccess && !isOwner && alsoWatched.count > 0;
  const unlockedFromArchive = hasAccess && !isOwner && isClosed && isPaidArchive(drop?.afterClose);

  const dropPrice = Number(drop?.price) || 0;
  const creditUnlockCost =
    drop && isPaidDrop(drop) && !isClosed ? freeUnlockCreditsForPrice(dropPrice) : null;
  const freeUnlocksLeft =
    creditSummary?.freeUnlocksRemaining ?? SPEND.freeUnlock.monthlyCap;
  const creditUnlockOffer = Boolean(openGate && creditUnlockCost && freeUnlocksLeft > 0);
  const canAffordCreditUnlock =
    creditUnlockCost != null && (creditSummary?.balance ?? 0) >= creditUnlockCost;

  const creatorName = creator?.displayName || "Creator";
  const creatorHandle = creator?.handle || creator?.username || "creator";
  const creatorAvatar = creator?.avatarUrl || creator?.avatar || "";
  const creatorLink = (creator?.handle || creator?.username)
    ? `/creator/${creator.handle || creator.username}`
    : "#";
  const nicheMeta = getNicheMeta(creator?.niche);
  const founding = isFoundingCreator(creator);

  /* Fog clear: keep overlay mounted while frosted class drops, so CSS opacity/filter tween */
  useEffect(() => {
    if (openGate) {
      setFrostOverlay(true);
      return undefined;
    }
    if (!frostOverlay) return undefined;
    if (prefersReduced) {
      setFrostOverlay(false);
      return undefined;
    }
    const t = setTimeout(() => setFrostOverlay(false), DURATION.fogClear * 1000);
    return () => clearTimeout(t);
  }, [openGate, prefersReduced]); // eslint-disable-line react-hooks/exhaustive-deps -- frostOverlay read only as gate

  /* ── Claim / record view ── */
  const claimDropAccess = async ({ successText } = {}) => {
    if (!currentUser || !drop) return false;
    const result = await recordDropView({ dropId: drop.id, userId: currentUser.uid });
    setViewRecorded(true);
    if (result?.keepsAccess || result?.alreadyViewed || result?.isFirstViewer) {
      const rec = await fetchFirstViewRecord(currentUser.uid, drop.id);
      if (rec) setFirstViewRecord(rec);
      else setFirstViewRecord({ dropId: drop.id, userId: currentUser.uid });
    }
    if (result?.dropClosed && !result?.keepsAccess && !result?.alreadyViewed) {
      setUnlockToast({ type: "error", text: "This window just closed — you missed it by a beat." });
      return false;
    }
    if (successText) setUnlockToast({ type: "success", text: successText });
    if (typeof result?.newViewCount === "number") {
      setDrop((prev) => (prev ? { ...prev, viewCount: result.newViewCount } : prev));
    }
    return true;
  };

  const handleFreeUnlock = async () => {
    if (!currentUser) {
      navigate("/welcome?auth=signin");
      return;
    }
    if (directUnlocking || !drop) return;
    setDirectUnlocking(true);
    try {
      await claimDropAccess({ successText: "Drop unlocked!" });
    } catch (err) {
      console.error("Free unlock failed:", err);
      setUnlockToast({ type: "error", text: err.message || "Failed to unlock drop" });
    } finally {
      setDirectUnlocking(false);
    }
  };

  const handleCreditUnlock = async () => {
    if (!currentUser || !drop || creditUnlocking || !creditUnlockCost) return;
    if (!canAffordCreditUnlock) {
      setUnlockToast({
        type: "error",
        text: `You need ${creditUnlockCost} credits. You have ${creditSummary?.balance ?? 0}.`,
      });
      return;
    }
    setCreditUnlocking(true);
    try {
      await unlockDropWithCredits(drop.id);
      setHasCreditUnlock(true);
      await claimDropAccess({ successText: `Unlocked with ${creditUnlockCost} credits!` });
      const summary = await fetchCreditSummary(
        currentUser.uid,
        userProfile?.role === "creator" || userProfile?.isCreator
      );
      setCreditSummary(summary);
    } catch (err) {
      setUnlockToast({ type: "error", text: err.message || "Credit unlock failed." });
    } finally {
      setCreditUnlocking(false);
    }
  };

  const openDirectPayModal = (kind, seed = null) => {
    setPayModalKind(kind);
    setPayModalSeed(seed);
    setShowUnlockChoice(true);
  };

  const handleCashUnlock = async () => {
    if (!currentUser) {
      navigate("/welcome?auth=signin");
      return;
    }
    if (!drop || directUnlocking) return;
    setDirectUnlocking(true);
    try {
      const result = await purchaseDropAccess({ dropId: drop.id, kind: "unlock" });
      if (result.status === "granted") {
        setHasPaidUnlock(true);
        await claimDropAccess({ successText: "Drop unlocked!" });
        return;
      }
      openDirectPayModal("unlock", {
        clientSecret: result.clientSecret,
        paymentIntentId: result.paymentIntentId,
        amountCents: result.amount,
        publishableKey: result.publishableKey,
      });
    } catch (err) {
      setUnlockToast({ type: "error", text: err.message || "Could not unlock drop." });
    } finally {
      setDirectUnlocking(false);
    }
  };

  const handleUnlockClick = () => {
    if (!currentUser) {
      navigate("/welcome?auth=signin");
      return;
    }
    if (!drop || directUnlocking) return;

    if (isFreeDrop(drop)) {
      handleFreeUnlock();
      return;
    }
    if (isSubscriptionDrop(drop) && hasRequiredTierAccess) {
      handleFreeUnlock();
      return;
    }
    if (isPaidDrop(drop)) {
      handleCashUnlock();
      return;
    }
    setTierModalInitial(null);
    setShowTierOverlay(true);
  };

  const handleArchiveUnlock = async () => {
    if (!currentUser) {
      navigate("/welcome?auth=signin");
      return;
    }
    if (!drop || directUnlocking) return;
    setDirectUnlocking(true);
    try {
      const result = await purchaseDropAccess({ dropId: drop.id, kind: "archive" });
      if (result.status === "granted") {
        setHasPaidUnlock(true);
        setUnlockToast({ type: "success", text: "Archive unlocked — yours forever." });
        return;
      }
      openDirectPayModal("archive", {
        clientSecret: result.clientSecret,
        paymentIntentId: result.paymentIntentId,
        amountCents: result.amount,
        publishableKey: result.publishableKey,
      });
    } catch (err) {
      setUnlockToast({ type: "error", text: err.message || "Could not unlock archive." });
    } finally {
      setDirectUnlocking(false);
    }
  };

  const handleFollow = async () => {
    if (!currentUser || !creator || followBusy) return;
    setFollowBusy(true);
    try {
      const next = await toggleFollow({
        followerId: currentUser.uid,
        creatorId: creator.id,
        isFollowing,
        creatorUsername: creator.handle || creator.username || "",
        creatorDisplayName: creator.displayName,
        creatorAvatar: creator.avatar || creator.avatarUrl,
      });
      setIsFollowing(next);
    } catch (err) {
      console.error("Follow error:", err);
    } finally {
      setFollowBusy(false);
    }
  };

  const handleLike = async () => {
    if (!currentUser) { navigate("/welcome?auth=signin"); return; }
    if (!drop || engagementBusy) return;
    setEngagementBusy(true);
    try {
      await toggleDropLike({ dropId: drop.id, userId: currentUser.uid, isLiked: liked });
      setLiked((p) => !p);
    } catch (err) {
      console.error("Drop like error:", err);
    } finally {
      setEngagementBusy(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser) { navigate("/welcome?auth=signin"); return; }
    if (!drop || engagementBusy) return;
    setEngagementBusy(true);
    try {
      await toggleDropSave({ dropId: drop.id, userId: currentUser.uid, isSaved: saved, dropData: drop });
      setSaved((p) => !p);
    } catch (err) {
      console.error("Drop save error:", err);
    } finally {
      setEngagementBusy(false);
    }
  };

  /* ── Sync first_views for fans who already took a deliberate unlock action.
      Free drops NEVER auto-claim — view must be confirmed via the gate button.
      Closed drops NEVER record first_views (archive buyers get access, not rank). ── */
  useEffect(() => {
    if (!drop || viewRecorded || !currentUser || accessLoading || isOwner) return;
    if (isClosed) return;
    if (isFreeDrop(drop) && !firstViewRecord) return;

    const entitled =
      bypassesContentPaywall(userProfile)
      || isSupporter
      || hasCreditUnlock
      || hasPaidUnlock
      || Boolean(userProfile?.unlockedDrops?.[drop.id])
      || Boolean(firstViewRecord)
      || hasRequiredTierAccess;

    if (!entitled) return;

    let cancelled = false;
    (async () => {
      try {
        const result = await recordDropView({ dropId: drop.id, userId: currentUser.uid });
        if (cancelled) return;
        setViewRecorded(true);
        if (result?.keepsAccess || result?.alreadyViewed || result?.isFirstViewer) {
          const rec = await fetchFirstViewRecord(currentUser.uid, drop.id);
          if (!cancelled && rec) setFirstViewRecord(rec);
        }
        if (!cancelled && typeof result?.newViewCount === "number") {
          setDrop((prev) => (prev ? { ...prev, viewCount: result.newViewCount } : prev));
        }
      } catch (err) {
        console.error("recordDropView:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [
    drop,
    currentUser,
    viewRecorded,
    accessLoading,
    isOwner,
    isClosed,
    userProfile,
    isSupporter,
    hasCreditUnlock,
    hasPaidUnlock,
    firstViewRecord,
    hasRequiredTierAccess,
  ]);

  /* ── Sheet + catalog swipe gestures (axis-locked) ── */
  const sheetClosedTranslate = () => {
    const sheet = sheetRef.current;
    if (!sheet) return 400;
    return Math.max(sheet.offsetHeight - SHEET_PEEK_PX, 0);
  };

  const sheetStyle = (() => {
    if (sheetOffset != null) {
      return {
        transform: `translateY(${sheetOffset}px)`,
        transition: "none",
      };
    }
    return {
      transition: prefersReduced ? "none" : `transform ${DURATION.sheet}s ${EASE_CSS.out}`,
    };
  })();

  const toggleSheet = () => setSheetOpen((v) => !v);

  const onHandleClick = (e) => {
    e.stopPropagation();
    if (suppressHandleClickRef.current) {
      suppressHandleClickRef.current = false;
      return;
    }
    toggleSheet();
  };

  const onViewportPointerDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    if (e.target.closest(".dv-edge-btn")) return;
    if (sheetOpen && e.target.closest(".dv-sheet-body")) return;

    const fromHandle = Boolean(e.target.closest(".dv-sheet-handle"));
    const startClosed = sheetClosedTranslate();

    /* Sheet already open + touch on the handle: do not capture yet.
       Wait for a clear downward pull before engaging drag-to-close so
       near-handle / ambiguous touches don't steal native body scroll
       or slam the sheet shut. Closed-sheet handle behavior is unchanged
       (capture immediately below). */
    if (sheetOpen && fromHandle) {
      dragRef.current = {
        x: e.clientX,
        y: e.clientY,
        axis: null,
        sheetStart: 0,
        moved: false,
        fromHandle: true,
        pointerId: e.pointerId,
        captureEl: e.currentTarget,
        deferOpenHandle: true,
      };
      return;
    }

    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      axis: null,
      sheetStart: sheetOpen ? 0 : startClosed,
      moved: false,
      fromHandle,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onViewportPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    drag.lastDx = dx;
    drag.lastDy = dy;

    if (drag.deferOpenHandle) {
      if (
        Math.abs(dx) < OPEN_HANDLE_CLOSE_INTENT_PX
        && Math.abs(dy) < OPEN_HANDLE_CLOSE_INTENT_PX
      ) {
        return;
      }
      /* Upward or sideways first → abandon; no capture, no sheet drag. */
      if (dy <= 0 || Math.abs(dx) >= Math.abs(dy)) {
        suppressHandleClickRef.current = true;
        dragRef.current = null;
        return;
      }
      /* Clear downward intent — now own the gesture and close-drag. */
      drag.deferOpenHandle = false;
      drag.axis = "y";
      suppressHandleClickRef.current = true;
      drag.captureEl?.setPointerCapture?.(drag.pointerId);
    }

    if (!drag.axis) {
      if (Math.abs(dx) > AXIS_LOCK_PX || Math.abs(dy) > AXIS_LOCK_PX) {
        let axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
        if (axis === "x" && (sheetOpen || catalogRef.current.length < 2)) axis = "none";
        drag.axis = axis;
      }
    }
    if (drag.axis === "y") {
      drag.moved = Math.abs(dy) > 6 || drag.moved;
      setSheetDragging(true);
      const closed = sheetClosedTranslate();
      const t = Math.max(0, Math.min(closed, drag.sheetStart + dy));
      setSheetOffset(t);
    } else if (drag.axis === "x") {
      drag.moved = Math.abs(dx) > 6 || drag.moved;
      setTrackDragging(true);
      setTrackOffset(dx);
    }
  };

  const onViewportPointerUp = (e) => {
    const drag = dragRef.current;
    if (!drag) return;

    /* Deferred open-handle gesture never engaged — leave sheet alone.
       If the finger moved at all, suppress the ensuing click toggle. */
    if (drag.deferOpenHandle) {
      const hadMove = Math.abs(drag.lastDx || 0) > 4 || Math.abs(drag.lastDy || 0) > 4;
      if (hadMove) suppressHandleClickRef.current = true;
      dragRef.current = null;
      return;
    }

    const endDx = drag.lastDx || 0;
    const endDy = drag.lastDy || 0;
    const { axis, moved, fromHandle, pointerId, captureEl } = drag;
    dragRef.current = null;
    setSheetDragging(false);
    setSheetOffset(null);
    setTrackDragging(false);
    setTrackOffset(0);

    if (pointerId != null && captureEl?.hasPointerCapture?.(pointerId)) {
      try {
        captureEl.releasePointerCapture(pointerId);
      } catch (_) {
        /* already released */
      }
    } else if (e?.currentTarget?.hasPointerCapture?.(e.pointerId)) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (_) {
        /* already released */
      }
    }

    if (axis === "y") {
      if (!moved && fromHandle) return;
      if (endDy < -SWIPE_Y_THRESHOLD) setSheetOpen(true);
      else if (endDy > SWIPE_Y_THRESHOLD) setSheetOpen(false);
    } else if (axis === "x") {
      const idx = catalogIndexRef.current;
      const len = catalogRef.current.length;
      if (endDx < -SWIPE_X_THRESHOLD && idx < len - 1) goNext();
      else if (endDx > SWIPE_X_THRESHOLD && idx > 0) goPrev();
    }
  };

  /* ── Loading / error / login ── */
  if (!currentUser) {
    return (
      <div className="dv-page dv-page--message">
        <div className="dv-signin-lock" aria-hidden="true"><LockIcon /></div>
        <h3>Sign in to view this drop</h3>
        <p>{creatorName}&apos;s drop is only visible to logged-in members.</p>
        <Link to="/welcome?auth=signup" className="dv-cta-btn-inline">Create free account</Link>
        <Link to="/welcome?auth=signin" className="dv-cta-link-inline">Log in</Link>
      </div>
    );
  }

  if (loading) {
    return <div className="dv-page dv-page--loading"><SkeletonCard /></div>;
  }

  if (!drop) {
    return (
      <div className="dv-page dv-page--message">
        <h3>Drop not found</h3>
        <p>This drop may have been removed.</p>
        <Link to="/gallery" className="dv-cta-btn-inline">Back to Gallery</Link>
      </div>
    );
  }

  if (accessLoading) {
    return <div className="dv-page dv-page--loading"><SkeletonCard /></div>;
  }

  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/drop/${drop.id}` : `/drop/${drop.id}`;

  const isClearingFog = !openGate && frostOverlay;
  const showFrostOverlay = openGate || frostOverlay;
  const showClearGlass = hasAccess && !openGate;

  /* Window glass class — frosted only while openGate; unlock removes it so CSS clears fog */
  const glassClass = [
    "dv-win-glass",
    openGate ? "frosted" : "",
    closedForever ? "sealed" : "",
    closedArchive ? "curtained" : "",
    isClearingFog ? "dv-win-glass--clearing" : "",
  ].filter(Boolean).join(" ");

  /* Status chip (left) + price chip (right, locked only) */
  let statusChip = null;
  let priceChip = null;
  if (isOwner) {
    statusChip = `Live · ${views}/${maxViews}`;
  } else if (closedForever) {
    statusChip = `Closed · ${maxViews}/${maxViews}`;
  } else if (closedArchive) {
    statusChip = `Archive · ${maxViews}/${maxViews}`;
  } else if (unlockedFromArchive) {
    statusChip = "Archive · yours";
  } else if (hasAccess) {
    statusChip = firstViewRecord?.rank
      ? `Open · #${firstViewRecord.rank} of ${maxViews}`
      : "Open";
  } else if (openGate) {
    statusChip = `Closed · ${views}/${maxViews}`;
    if (isFreeDrop(drop)) priceChip = "Free";
    else if (isSubscriptionDrop(drop)) priceChip = "Subscribers";
    else priceChip = `${priceLabel(drop)} to open`;
  }

  const renderActionBar = () => (
    <div className="dv-action-bar">
      <button
        type="button"
        className={`dv-a-btn${liked ? " liked" : ""}`}
        onClick={handleLike}
        disabled={engagementBusy}
      >
        <HeartIcon /> Loved
      </button>
      <button
        type="button"
        className={`dv-a-btn${saved ? " saved" : ""}`}
        onClick={handleSave}
        disabled={engagementBusy}
      >
        <KeepIcon /> Keep
      </button>
      {!isOwner && (
        <button type="button" className="dv-a-btn tip" onClick={() => setTipOpen(true)}>
          <TipIcon /> Tip
        </button>
      )}
      <button type="button" className="dv-a-btn" onClick={() => setShareOpen(true)}>
        <Share2 size={16} strokeWidth={1.8} /> Share
      </button>
    </div>
  );

  const renderFollowRow = () => {
    if (isOwner) return null;
    return (
      <div className="dv-sheet-follow">
        <button
          type="button"
          className={`dv-sill-follow-btn${isFollowing ? " following" : ""}`}
          onClick={handleFollow}
          disabled={followBusy}
        >
          {isFollowing ? `Following ${creatorName}` : `Follow ${creatorName}`}
        </button>
      </div>
    );
  };

  const renderLockedSheet = () => {
    let priceRowLabel = "To open this window";
    let priceRowValue = priceLabel(drop);
    let primaryLabel = `Open window · ${priceLabel(drop)}`;
    let primaryAction = handleUnlockClick;
    let secondary = null;

    if (isFreeDrop(drop)) {
      priceRowLabel = "This one's on the house";
      priceRowValue = "Free";
      primaryLabel = directUnlocking ? "Opening…" : "Open window · free";
      primaryAction = handleFreeUnlock;
    } else if (isSubscriptionDrop(drop)) {
      priceRowLabel = "Subscribers only";
      const tierPrice = drop?.tierPrice || drop?.subscriptionPrice || drop?.price;
      priceRowValue = tierPrice ? `$${Number(tierPrice).toFixed(0)}/mo` : "Subscribe";
      primaryLabel = directUnlocking ? "Opening…" : `Subscribe to open · ${priceRowValue}`;
      primaryAction = () => {
        setTierModalInitial(null);
        setShowTierOverlay(true);
      };
      secondary = (
        <div className="dv-sill-secondary-note">
          opens every window from {creatorName}
        </div>
      );
    } else if (isPaidDrop(drop)) {
      primaryLabel = directUnlocking ? "Opening…" : `Open window · ${priceLabel(drop)}`;
      primaryAction = handleCashUnlock;
      if (creditUnlockOffer) {
        secondary = (
          <button
            type="button"
            className="dv-sill-credits-link"
            onClick={handleCreditUnlock}
            disabled={creditUnlocking || !canAffordCreditUnlock}
          >
            {creditUnlocking
              ? "Redeeming…"
              : `or redeem ${formatCount(creditUnlockCost)} credits`}
          </button>
        );
      }
    }

    return (
      <>
        <div className="dv-sill-price-row">
          <span className="dv-sill-label">{priceRowLabel}</span>
          <span className="dv-sill-price">{priceRowValue}</span>
        </div>
        <button
          type="button"
          className="dv-sill-primary-btn"
          onClick={primaryAction}
          disabled={directUnlocking}
        >
          <LockIcon size={14} />
          {primaryLabel}
        </button>
        {secondary}
        <div className="dv-sill-microcopy">
          {viewsLeft} spot{viewsLeft === 1 ? "" : "s"} left in the first {maxViews}
        </div>
        <div className="dv-progress-block">
          <div className="dv-progress-row">
            <span><strong>{views}</strong> of {maxViews} views taken</span>
            <span>{viewsLeft} remain</span>
          </div>
          <div className="dv-progress-bar">
            <div className="dv-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="dv-progress-hand">every view is someone&apos;s first and only time</span>
        </div>
        <div className="dv-d-title">{drop.title}</div>
        <DropCommentsSection
          dropId={drop.id}
          creatorId={drop.creatorId}
          creatorName={creatorName}
          creatorAvatar={creatorAvatar}
          hasAccess={false}
          title="What people are saying"
          limit={3}
          readOnly
          hideSub
          modNote="Open the window to see it yourself, and to join in."
        />
        {renderFollowRow()}
        {!isOwner && (
          <DropReportAction
            dropId={drop.id}
            creatorName={creatorName}
            onReported={() => setUnlockToast({ type: "success", text: "Reported — thanks for flagging this" })}
            onError={(err) => setUnlockToast({ type: "error", text: err.message || "Could not submit report." })}
          />
        )}
      </>
    );
  };

  const renderUnlockedSheet = () => (
    <>
      {unlockedFromArchive ? (
        <>
          <div className="dv-sill-owned-txt">From the archive</div>
          <span className="dv-sill-owned-sub">no window number, but yours to keep</span>
        </>
      ) : (
        <>
          <div className="dv-sill-owned-txt">
            {firstViewRecord?.rank
              ? `Your window, #${firstViewRecord.rank} of ${maxViews}`
              : "Your window"}
          </div>
          <span className="dv-sill-owned-sub">opened permanently, yours forever</span>
        </>
      )}
      {showAlsoWatched && (
        <div className="dv-network-row">
          <div className="dv-nw-avatars">
            {alsoWatched.viewers.map((viewer) => (
              <Avatar
                key={viewer.userId}
                src={viewer.avatarUrl || viewer.avatar || ""}
                name={viewer.displayName || "Fan"}
                className="dv-nw-av"
              />
            ))}
          </div>
          <div className="dv-nw-text">
            <strong>{alsoWatched.count} people you follow</strong> opened this window too
          </div>
        </div>
      )}
      <div className="dv-d-title">{drop.title}</div>
      {drop.description && <div className="dv-d-caption">{drop.description}</div>}
      {drop.contentType === "article" && (drop.mediaUrl || drop.articleBody) && (
        <div className="dv-article-body">{drop.mediaUrl || drop.articleBody}</div>
      )}
      {renderActionBar()}
      {renderFollowRow()}
      {!isOwner && (
        <DropReportAction
          dropId={drop.id}
          creatorName={creatorName}
          onReported={() => setUnlockToast({ type: "success", text: "Reported — thanks for flagging this" })}
          onError={(err) => setUnlockToast({ type: "error", text: err.message || "Could not submit report." })}
        />
      )}
      <DropCommentsSection
        dropId={drop.id}
        creatorId={drop.creatorId}
        creatorName={creatorName}
        creatorAvatar={creatorAvatar}
        hasAccess={hasAccess}
      />
      <div className="dv-info-card">
        <div className="dv-info-row">
          <span className="dv-info-label">Content type</span>
          <span className="dv-info-val">{contentTypeLabel(drop.contentType)}</span>
        </div>
        <div className="dv-info-row">
          <span className="dv-info-label">Price</span>
          <span className="dv-info-val gold">{priceLabel(drop)}</span>
        </div>
        <div className="dv-info-row">
          <span className="dv-info-label">Posted</span>
          <span className="dv-info-val">{timeAgo(drop.createdAt)}</span>
        </div>
        <div className="dv-info-row">
          <span className="dv-info-label">Access</span>
          <span className="dv-info-val">{accessLabel(drop)}</span>
        </div>
      </div>
    </>
  );

  const renderOwnerSheet = () => (
    <>
      <div className="dv-sill-owned-txt">Your window is open</div>
      <span className="dv-sill-owned-sub">
        {views} of {maxViews}
        {closingSoon ? " — closing soon" : ` — ${viewsLeft} spots left`}
      </span>
      <div className="dv-progress-block">
        <div className="dv-progress-row">
          <span><strong>{views}</strong> of {maxViews} views taken</span>
          <span>{viewsLeft} remain</span>
        </div>
        <div className="dv-progress-bar">
          <div className="dv-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="dv-progress-hand">every view is someone&apos;s first and only time</span>
      </div>
      <div className="dv-d-title">
        {drop.title}
        <Link to={`/post?edit=${drop.id}`} className="dv-edit-link">Edit →</Link>
      </div>
      {drop.description && <div className="dv-d-caption">{drop.description}</div>}
      {drop.contentType === "article" && (drop.mediaUrl || drop.articleBody) && (
        <div className="dv-article-body">{drop.mediaUrl || drop.articleBody}</div>
      )}
      {renderActionBar()}
      <DropCommentsSection
        dropId={drop.id}
        creatorId={drop.creatorId}
        creatorName={creatorName}
        creatorAvatar={creatorAvatar}
        hasAccess={hasAccess}
        modNote="Comments are visible to you here — moderate them from Manage Drops"
      />
    </>
  );

  const renderClosedForeverSheet = () => (
    <>
      <div className="dv-sill-closed-title">This window has closed for good</div>
      <div className="dv-sill-closed-sub">
        {maxViews} people got there first — that&apos;s the whole game. {creatorName} posts often; be faster next time.
      </div>
      <button
        type="button"
        className="dv-sill-follow-btn"
        onClick={handleFollow}
        disabled={followBusy}
      >
        {isFollowing ? "Following" : `Follow ${creatorName}`}
      </button>
      <div className="dv-d-title">{drop.title}</div>
    </>
  );

  const renderClosedArchiveSheet = () => (
    <>
      <div className="dv-sill-closed-title">The first 100 has closed, but this one&apos;s still here</div>
      <div className="dv-sill-closed-sub">
        No window number attached this time, just the moment itself.
      </div>
      <button
        type="button"
        className="dv-sill-archive-btn"
        onClick={handleArchiveUnlock}
        disabled={directUnlocking}
      >
        {directUnlocking
          ? "Unlocking…"
          : `Unlock the archive · ${archivePriceLabel(drop)}`}
      </button>
      {renderFollowRow()}
      <div className="dv-d-title">{drop.title}</div>
    </>
  );

  let sheetBody;
  if (isOwner) sheetBody = renderOwnerSheet();
  else if (closedForever) sheetBody = renderClosedForeverSheet();
  else if (closedArchive) sheetBody = renderClosedArchiveSheet();
  else if (openGate) sheetBody = renderLockedSheet();
  else sheetBody = renderUnlockedSheet();

  const prevCatalogDrop = catalogIndex > 0 ? catalog[catalogIndex - 1] : null;
  const nextCatalogDrop = catalogIndex >= 0 && catalogIndex < catalog.length - 1
    ? catalog[catalogIndex + 1]
    : null;
  const trackCurrentSlot = prevCatalogDrop ? 1 : 0;
  const trackStyle = {
    transform: `translateX(calc(${-trackCurrentSlot * 100}% + ${trackOffset}px))`,
    transition: trackDragging || prefersReduced
      ? "none"
      : `transform ${DURATION.sheet}s ${EASE_CSS.out}`,
  };
  const canGoPrev = catalogIndex > 0;
  const canGoNext = catalogIndex >= 0 && catalogIndex < catalog.length - 1;
  const showCatalogChrome = catalog.length > 1;

  const renderPreviewSlide = (previewDrop) => {
    if (!previewDrop) return null;
    const src = previewThumb(previewDrop);
    return (
      <div className="dv-drop-slide dv-drop-slide--preview" key={previewDrop.id} aria-hidden="true">
        {src ? (
          <img className="dv-drop-preview-img" src={src} alt="" loading="lazy" decoding="async" />
        ) : (
          <div className="dv-drop-preview-fallback" />
        )}
        <div className="dv-drop-preview-dim" />
      </div>
    );
  };

  return (
    <div className="dv-page dv-page--window">
      <div className="main-col">
        <div
          className="dv-viewport"
          ref={viewportRef}
          onPointerDown={onViewportPointerDown}
          onPointerMove={onViewportPointerMove}
          onPointerUp={onViewportPointerUp}
          onPointerCancel={onViewportPointerUp}
        >
          <div className="dv-app-topbar">
            <div className="dv-app-word">100<em>Views</em>Only</div>
            <Link to="/gallery" className="dv-app-back">← Gallery</Link>
          </div>

          <div
            className={`dv-drop-track${trackDragging ? " dragging" : ""}`}
            style={trackStyle}
          >
            {renderPreviewSlide(prevCatalogDrop)}
            <div className="dv-drop-slide dv-drop-slide--current">
              <div className={glassClass}>
                <div className="dv-win-media">
                  <DropMedia drop={drop} creator={creator} locked={gated} />
                </div>
                {showFrostOverlay && <div className="dv-frost-overlay" />}
                {closedForever && <div className="dv-seal-overlay" />}
                {closedForever && (
                  <div className="dv-win-closed-label">this window has closed</div>
                )}
                {openGate && (
                  <div className="dv-win-latch" aria-hidden="true">
                    <LockIcon size={14} />
                  </div>
                )}
                <WatermarkOverlay creator={creator} visible={showClearGlass} />

                <Link to={creatorLink} className="dv-slide-creator">
                  <Avatar src={creatorAvatar} name={creatorName} className="dv-slide-c-av" />
                  <span className="dv-slide-c-name">{creatorName}</span>
                </Link>

                {statusChip && <div className="dv-win-status">{statusChip}</div>}
                {priceChip && <div className="dv-win-price-chip">{priceChip}</div>}
              </div>
            </div>
            {renderPreviewSlide(nextCatalogDrop)}
          </div>

          {showCatalogChrome && (
            <>
              <div className="dv-dots" aria-hidden="true">
                {catalog.map((row, i) => (
                  <span
                    key={row.id}
                    className={`dv-dot${i === catalogIndex ? " active" : ""}`}
                  />
                ))}
              </div>
              <button
                type="button"
                className="dv-edge-btn left"
                aria-label="Previous drop"
                disabled={!canGoPrev}
                onClick={(e) => {
                  e.stopPropagation();
                  goPrev();
                }}
              >
                ‹
              </button>
              <button
                type="button"
                className="dv-edge-btn right"
                aria-label="Next drop"
                disabled={!canGoNext}
                onClick={(e) => {
                  e.stopPropagation();
                  goNext();
                }}
              >
                ›
              </button>
            </>
          )}

          <div
            className={`dv-bottom-sheet${sheetDragging ? " dragging" : ""}${sheetOpen ? " is-open" : ""}`}
            ref={sheetRef}
            style={sheetStyle}
          >
            <button
              type="button"
              className="dv-sheet-handle"
              aria-expanded={sheetOpen}
              aria-controls="dv-sheet-body"
              onClick={onHandleClick}
            >
              <span className="dv-sheet-bar" />
              {!sheetOpen && (
                <span className="dv-sheet-peek-label">Swipe up for details</span>
              )}
            </button>
            <div className="dv-sheet-body" id="dv-sheet-body">
              {sheetBody}
            </div>
          </div>
        </div>
      </div>

      <div className="sidebar-col">
        <div className="sb-card">
          <Link to={creatorLink} className="sb-creator-top">
            <Avatar src={creatorAvatar} name={creatorName} className="sb-av" />
            <div>
              <div className="sb-name">{creatorName}</div>
              <div className="sb-handle">@{creatorHandle}</div>
            </div>
          </Link>
          {creator?.bio && <div className="sb-bio">{creator.bio}</div>}
          <div className="sb-badges">
            {nicheMeta?.label && (
              <span className="sb-badge"><NicheIcon niche={nicheMeta.key} size={11} /> {nicheMeta.label}</span>
            )}
            {founding && <span className="sb-badge founding">Founding Creator</span>}
          </div>
          <div className="sb-stats-row">
            <div className="sb-stat">
              <div className="sb-stat-num">{formatCount(creator?.followerCount || creator?.subscriberCount || 0)}</div>
              <div className="sb-stat-lbl">Subscribers</div>
            </div>
            <div className="sb-stat">
              <div className="sb-stat-num">{creatorDropCount}</div>
              <div className="sb-stat-lbl">Drops</div>
            </div>
          </div>
          {!isOwner && (
            <button type="button" className="sb-follow-btn" onClick={handleFollow} disabled={followBusy}>
              {isFollowing ? "Following" : "Follow"}
            </button>
          )}
        </div>

        <div className="sb-card">
          {isClosed ? (
            <>
              <div className="sb-progress-num">{maxViews}</div>
              <div className="sb-progress-lbl">of {maxViews} — closed</div>
              <div className="sb-progress-track">
                <div className="sb-progress-fill closed" style={{ width: "100%" }} />
              </div>
              <div className="sb-progress-foot"><span>This one&apos;s done</span></div>
            </>
          ) : (
            <>
              <div className="sb-progress-num">{views}</div>
              <div className="sb-progress-lbl">of {maxViews} first views taken</div>
              <div className="sb-progress-track">
                <div className="sb-progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <div className="sb-progress-foot">
                <span>{viewsLeft} spots remaining</span>
                {closingSoon && <strong>Closing</strong>}
              </div>
            </>
          )}
          <span className="sb-progress-hand">every view is someone&apos;s first and only time</span>
        </div>
      </div>

      <DropUnlockChoiceModal
        isOpen={showUnlockChoice}
        drop={drop}
        creatorName={creatorName}
        directPayOnly
        kind={payModalKind}
        clientSecret={payModalSeed?.clientSecret || null}
        paymentIntentId={payModalSeed?.paymentIntentId || null}
        amountCents={payModalSeed?.amountCents ?? null}
        publishableKey={payModalSeed?.publishableKey || null}
        onDismiss={() => {
          setShowUnlockChoice(false);
          setPayModalSeed(null);
        }}
        onUnlocked={async (result) => {
          setHasPaidUnlock(true);
          setShowUnlockChoice(false);
          setPayModalSeed(null);
          if (result?.kind === "archive" || payModalKind === "archive") {
            setUnlockToast({ type: "success", text: "Archive unlocked — yours forever." });
            return;
          }
          try {
            await claimDropAccess({ successText: "Drop unlocked!" });
          } catch (err) {
            console.warn("Post-purchase view claim:", err);
            setUnlockToast({ type: "success", text: "Drop unlocked!" });
          }
        }}
      />

      <TierSubscriptionModal
        isOpen={showTierOverlay}
        creatorId={drop.creatorId}
        creatorName={creatorName}
        currentSubscriptionTier={userProfile?.subscriptionTiers?.[drop.creatorId] || null}
        initialTier={tierModalInitial}
        onDismiss={() => {
          setShowTierOverlay(false);
          setTierModalInitial(null);
        }}
        onSubscribe={async (result) => {
          setShowTierOverlay(false);
          setTierModalInitial(null);
          if (result?.action === "unsubscribe") {
            setUnlockToast({ type: "success", text: "Subscription canceled." });
            return;
          }
          if (result?.tier) {
            setIsSupporter(true);
            try {
              await claimDropAccess({ successText: `You're in the ${result.tier} circle — drop unlocked!` });
            } catch {
              setUnlockToast({ type: "success", text: `You're in the ${result.tier} circle.` });
            }
          }
        }}
      />

      {creator && (
        <TipJarSheet
          open={tipOpen}
          onClose={() => setTipOpen(false)}
          creator={creator}
          dropId={drop.id}
          currentUserId={currentUser?.uid}
          userEmail={currentUser?.email || ""}
        />
      )}

      <ShareModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        url={shareUrl}
        title={drop.title}
      />

      {unlockToast && (
        <div className={`dv-toast dv-toast--${unlockToast.type}`}>{unlockToast.text}</div>
      )}
    </div>
  );
}
